import {
    PersistentEventTypes,
    PresentationEventTypes,
    RevisionEventTypes,
    SocketEventTypes,
    CommandEventTypes,
    OTTransformEventTypes,
    CollaborationEventTypes,
} from './event-types';
import type { RecoveryStatus } from '@tencent/slide-sdk-persistent';
import type { MutationSource } from '../../constants/mutation-constants';
import type {
    CollaborationEventsMap,
    CommandEventsMap,
    OTTransformEventsMap,
    PersistentEventMap,
    PresentationEventsMap,
    RevisionEventMap,
    SocketEventsMap,
} from './event-map';

type Off = () => void;
type EventsMap = { [event: string]: any } & PresentationEventsMap &
    SocketEventsMap &
    RevisionEventMap &
    PersistentEventMap &
    CommandEventsMap &
    OTTransformEventsMap &
    CollaborationEventsMap;

export interface IEventManager {
    dataModelEvents: {
        onPageLoadingStateChange(cb: (isLoading: boolean) => void): Off;
    };

    socketEvents: {
        onReceiveMessage(cb: (...args: Parameters<EventsMap[SocketEventTypes.ReceiveMessage]>) => void): Off;
        onRevertMessage(cb: (...args: Parameters<EventsMap[SocketEventTypes.RevertMessage]>) => void): Off;
        onChangeToOnline(cb: (...args: Parameters<EventsMap[SocketEventTypes.ChangeToOnline]>) => void): Off;
        onChangeToOffline(cb: (...args: Parameters<EventsMap[SocketEventTypes.ChangeToOffline]>) => void): Off;
        onShutDown(cb: (...args: Parameters<EventsMap[SocketEventTypes.ShutDown]>) => void): Off;
        onDeleteDoc(cb: (...args: Parameters<EventsMap[SocketEventTypes.DeleteDoc]>) => void): Off;
        onTitleChange(cb: (...args: Parameters<EventsMap[SocketEventTypes.TitleMessage]>) => void): Off;
        onUserEnter(cb: (...args: Parameters<EventsMap[SocketEventTypes.UserEnter]>) => void): Off;
        onUserLeave(cb: (...args: Parameters<EventsMap[SocketEventTypes.UserLeave]>) => void): Off;
        onAllLockInfo(cb: (...args: Parameters<EventsMap[SocketEventTypes.AllLockInfo]>) => void): Off;
        onUnlockInfo(cb: (...args: Parameters<EventsMap[SocketEventTypes.UnLockInfo]>) => void): Off;
        onCursorMessage(cb: (...args: Parameters<EventsMap[SocketEventTypes.CursorMessage]>) => void): Off;
        onWaterMarkChange(cb: (...args: Parameters<EventsMap[SocketEventTypes.WaterMarkChange]>) => void): Off;
        onRightMessage(cb: (...args: Parameters<EventsMap[SocketEventTypes.RightMessage]>) => void): Off;
        onPresentInfo(cb: (...args: Parameters<EventsMap[SocketEventTypes.PresentInfo]>) => void): Off;
        onAnonymousChange(cb: (...args: Parameters<EventsMap[SocketEventTypes.AnonymousChange]>) => void): Off;
        onWebOfficeRevert(cb: (...args: Parameters<EventsMap[SocketEventTypes.WebOfficeRevert]>) => void): Off;
        onCommitUpload(cb: (...args: Parameters<EventsMap[SocketEventTypes.CommitUpload]>) => void): Off;
    };

    mutationEvents: {
        onCommitCommand(cb: (source: MutationSource) => void): Off;
        onCommitTransaction(
            cb: (...args: Parameters<EventsMap[PresentationEventTypes.CommitTransaction]>) => void,
        ): Off;
        onSubmitMutations(cb: (...args: Parameters<EventsMap[PresentationEventTypes.SubmitMutations]>) => void): Off;
        onMutationApplied(cb: (...args: Parameters<EventsMap[PresentationEventTypes.MutationsApplied]>) => void): Off;
        onMutationError(cb: (...args: Parameters<EventsMap[PresentationEventTypes.MutationError]>) => void): Off;
    };

    persistentEvents: {
        onRecoveryStatusChange(cb: (isRecovering: RecoveryStatus) => void): Off;
        onDataBaseFatalError(
            cb: (...args: Parameters<EventsMap[PersistentEventTypes.DataBaseFatalError]>) => void,
        ): Off;
        onAddOrUpdateData(cb: (...args: Parameters<EventsMap[PersistentEventTypes.AddOrUpdateData]>) => void): Off;
        onRemoveData(cb: (...args: Parameters<EventsMap[PersistentEventTypes.RemoveData]>) => void): Off;
        onOpenDataBaseError(cb: (...args: Parameters<EventsMap[PersistentEventTypes.OpenDataBaseError]>) => void): Off;
        onReplaceAllData(cb: (...args: Parameters<EventsMap[PersistentEventTypes.ReplaceAllData]>) => void): Off;
    };

    revisionEvents: {
        onCommitSucceed(cb: (...args: Parameters<EventsMap[RevisionEventTypes.CommitSucceed]>) => void): Off;
        onCommitFail(cb: (...args: Parameters<EventsMap[RevisionEventTypes.CommitFail]>) => void): Off;
        onChangeToOnline(cb: (...args: Parameters<EventsMap[RevisionEventTypes.ChangeToOnline]>) => void): Off;
        onChangeToOffline(cb: (...args: Parameters<EventsMap[RevisionEventTypes.ChangeToOffline]>) => void): Off;
        onEnqueue(cb: (...args: Parameters<EventsMap[RevisionEventTypes.Enqueue]>) => void): Off;
        onFetchMissingDataError(
            cb: (...args: Parameters<EventsMap[RevisionEventTypes.FetchMissingDataError]>) => void,
        ): Off;
        onUpdateBaseRevision(cb: (...args: Parameters<EventsMap[RevisionEventTypes.UpdateBaseRevision]>) => void): Off;
        onBaseVersionToLow(cb: (...args: Parameters<EventsMap[RevisionEventTypes.BaseVersionToLow]>) => void): Off;
    };

    commandEvents: {
        onUndoRedoChange(cb: (...args: Parameters<EventsMap[CommandEventTypes.UndoRedoChange]>) => void): Off;
        onClearCommand(cb: (...args: Parameters<EventsMap[CommandEventTypes.ClearCommand]>) => void): Off;
    };

    otTransformEvents: {
        onTransformError(cb: (...args: Parameters<EventsMap[OTTransformEventTypes.TransformError]>) => void): Off;
        onTransformTimeout(cb: (...args: Parameters<EventsMap[OTTransformEventTypes.TransformTimeout]>) => void): Off;
    };

    collaboratorEvents: {
        onFatalError(cb: (...args: Parameters<EventsMap[CollaborationEventTypes.FatalError]>) => void): Off;
        onStatusChange(cb: (...args: Parameters<EventsMap[CollaborationEventTypes.StatusChange]>) => void): Off;
    };

    on<K extends keyof EventsMap>(event: K, cb: EventsMap[K]): () => void;
    once<K extends keyof EventsMap>(event: K, cb: EventsMap[K]): void;
    emit<K extends keyof EventsMap>(event: K, ...args: Parameters<EventsMap[K]>): void;
    off<K extends keyof EventsMap>(event: K, cb?: EventsMap[K]): void;
    dispose(): void;
}

export class EventManager implements IEventManager {
    private handler: Partial<{ [k in keyof EventsMap]: EventsMap[k][] }> = {};

    public get dataModelEvents() {
        return {
            onPageLoadingStateChange: (cb: (isLoading: boolean) => void) =>
                this.on(PresentationEventTypes.PageLoadingStateChange, cb),
        };
    }

    public get socketEvents() {
        return {
            onReceiveMessage: (cb: (...args: Parameters<EventsMap[SocketEventTypes.ReceiveMessage]>) => void) =>
                this.on(SocketEventTypes.ReceiveMessage, cb),
            onRevertMessage: (cb: (...args: Parameters<EventsMap[SocketEventTypes.RevertMessage]>) => void) =>
                this.on(SocketEventTypes.RevertMessage, cb),
            onChangeToOnline: (cb: (...args: Parameters<EventsMap[SocketEventTypes.ChangeToOnline]>) => void) =>
                this.on(SocketEventTypes.ChangeToOnline, cb),
            onChangeToOffline: (cb: (...args: Parameters<EventsMap[SocketEventTypes.ChangeToOffline]>) => void) =>
                this.on(SocketEventTypes.ChangeToOffline, cb),
            onShutDown: (cb: (...args: Parameters<EventsMap[SocketEventTypes.ShutDown]>) => void) =>
                this.on(SocketEventTypes.ShutDown, cb),
            onDeleteDoc: (cb: (...args: Parameters<EventsMap[SocketEventTypes.DeleteDoc]>) => void) =>
                this.on(SocketEventTypes.DeleteDoc, cb),
            onTitleChange: (cb: (...args: Parameters<EventsMap[SocketEventTypes.TitleMessage]>) => void) =>
                this.on(SocketEventTypes.TitleMessage, cb),
            onUserEnter: (cb: (...args: Parameters<EventsMap[SocketEventTypes.UserEnter]>) => void) =>
                this.on(SocketEventTypes.UserEnter, cb),
            onUserLeave: (cb: (...args: Parameters<EventsMap[SocketEventTypes.UserLeave]>) => void) =>
                this.on(SocketEventTypes.UserLeave, cb),
            onAllLockInfo: (cb: (...args: Parameters<EventsMap[SocketEventTypes.AllLockInfo]>) => void) =>
                this.on(SocketEventTypes.AllLockInfo, cb),
            onUnlockInfo: (cb: (...args: Parameters<EventsMap[SocketEventTypes.UnLockInfo]>) => void) =>
                this.on(SocketEventTypes.UnLockInfo, cb),
            onCursorMessage: (cb: (...args: Parameters<EventsMap[SocketEventTypes.CursorMessage]>) => void) =>
                this.on(SocketEventTypes.CursorMessage, cb),
            onWaterMarkChange: (cb: (...args: Parameters<EventsMap[SocketEventTypes.WaterMarkChange]>) => void) =>
                this.on(SocketEventTypes.WaterMarkChange, cb),
            onRightMessage: (cb: (...args: Parameters<EventsMap[SocketEventTypes.RightMessage]>) => void) =>
                this.on(SocketEventTypes.RightMessage, cb),
            onPresentInfo: (cb: (...args: Parameters<EventsMap[SocketEventTypes.PresentInfo]>) => void) =>
                this.on(SocketEventTypes.PresentInfo, cb),
            onAnonymousChange: (cb: (...args: Parameters<EventsMap[SocketEventTypes.AnonymousChange]>) => void) =>
                this.on(SocketEventTypes.AnonymousChange, cb),
            onWebOfficeRevert: (cb: (...args: Parameters<EventsMap[SocketEventTypes.WebOfficeRevert]>) => void) =>
                this.on(SocketEventTypes.WebOfficeRevert, cb),
            onCommitUpload: (cb: (...args: Parameters<EventsMap[SocketEventTypes.CommitUpload]>) => void) =>
                this.on(SocketEventTypes.CommitUpload, cb),
        };
    }

    public get mutationEvents() {
        return {
            onCommitCommand: (cb: (source: MutationSource) => void) =>
                this.on(PresentationEventTypes.CommitCommand, (_, __, source: MutationSource) => cb(source)),
            onCommitTransaction: (
                cb: (...args: Parameters<EventsMap[PresentationEventTypes.CommitTransaction]>) => void,
            ) => this.on(PresentationEventTypes.CommitTransaction, cb),
            onSubmitMutations: (cb: (...args: Parameters<EventsMap[PresentationEventTypes.SubmitMutations]>) => void) =>
                this.on(PresentationEventTypes.SubmitMutations, cb),
            onMutationApplied: (
                cb: (...args: Parameters<EventsMap[PresentationEventTypes.MutationsApplied]>) => void,
            ) => this.on(PresentationEventTypes.MutationsApplied, cb),
            onMutationError: (cb: (...args: Parameters<EventsMap[PresentationEventTypes.MutationError]>) => void) =>
                this.on(PresentationEventTypes.MutationError, cb),
        };
    }

    public get persistentEvents() {
        return {
            onRecoveryStatusChange: (cb: (isRecovering: RecoveryStatus) => void) =>
                this.on(PersistentEventTypes.RecoveryStatusChange, cb),
            onDataBaseFatalError: (
                cb: (...args: Parameters<EventsMap[PersistentEventTypes.DataBaseFatalError]>) => void,
            ) => this.on(PersistentEventTypes.DataBaseFatalError, cb),
            onAddOrUpdateData: (cb: (...args: Parameters<EventsMap[PersistentEventTypes.AddOrUpdateData]>) => void) =>
                this.on(PersistentEventTypes.AddOrUpdateData, cb),
            onRemoveData: (cb: (...args: Parameters<EventsMap[PersistentEventTypes.RemoveData]>) => void) =>
                this.on(PersistentEventTypes.RemoveData, cb),
            onOpenDataBaseError: (
                cb: (...args: Parameters<EventsMap[PersistentEventTypes.OpenDataBaseError]>) => void,
            ) => this.on(PersistentEventTypes.OpenDataBaseError, cb),
            onReplaceAllData: (cb: (...args: Parameters<EventsMap[PersistentEventTypes.ReplaceAllData]>) => void) =>
                this.on(PersistentEventTypes.ReplaceAllData, cb),
        };
    }

    public get revisionEvents() {
        return {
            onCommitSucceed: (cb: (...args: Parameters<EventsMap[RevisionEventTypes.CommitSucceed]>) => void) =>
                this.on(RevisionEventTypes.CommitSucceed, cb),
            onCommitFail: (cb: (...args: Parameters<EventsMap[RevisionEventTypes.CommitFail]>) => void) =>
                this.on(RevisionEventTypes.CommitFail, cb),
            onChangeToOnline: (cb: (...args: Parameters<EventsMap[RevisionEventTypes.ChangeToOnline]>) => void) =>
                this.on(RevisionEventTypes.ChangeToOnline, cb),
            onChangeToOffline: (cb: (...args: Parameters<EventsMap[RevisionEventTypes.ChangeToOffline]>) => void) =>
                this.on(RevisionEventTypes.ChangeToOffline, cb),
            onEnqueue: (cb: (...args: Parameters<EventsMap[RevisionEventTypes.Enqueue]>) => void) =>
                this.on(RevisionEventTypes.Enqueue, cb),
            onFetchMissingDataError: (
                cb: (...args: Parameters<EventsMap[RevisionEventTypes.FetchMissingDataError]>) => void,
            ) => this.on(RevisionEventTypes.FetchMissingDataError, cb),
            onUpdateBaseRevision: (
                cb: (...args: Parameters<EventsMap[RevisionEventTypes.UpdateBaseRevision]>) => void,
            ) => this.on(RevisionEventTypes.UpdateBaseRevision, cb),
            onBaseVersionToLow: (cb: (...args: Parameters<EventsMap[RevisionEventTypes.BaseVersionToLow]>) => void) =>
                this.on(RevisionEventTypes.BaseVersionToLow, cb),
        };
    }

    public get commandEvents() {
        return {
            onUndoRedoChange: (cb: (...args: Parameters<EventsMap[CommandEventTypes.UndoRedoChange]>) => void) =>
                this.on(CommandEventTypes.UndoRedoChange, cb),
            onRedo: (cb: (...args: Parameters<EventsMap[CommandEventTypes.Redo]>) => void) =>
                this.on(CommandEventTypes.Redo, cb),
            onUndo: (cb: (...args: Parameters<EventsMap[CommandEventTypes.Undo]>) => void) =>
                this.on(CommandEventTypes.Undo, cb),
            onClearCommand: (cb: (...args: Parameters<EventsMap[CommandEventTypes.ClearCommand]>) => void) =>
                this.on(CommandEventTypes.ClearCommand, cb),
        };
    }

    public get otTransformEvents() {
        return {
            onTransformError: (cb: (...args: Parameters<EventsMap[OTTransformEventTypes.TransformError]>) => void) =>
                this.on(OTTransformEventTypes.TransformError, cb),
            onTransformTimeout: (
                cb: (...args: Parameters<EventsMap[OTTransformEventTypes.TransformTimeout]>) => void,
            ) => this.on(OTTransformEventTypes.TransformTimeout, cb),
        };
    }

    public get collaboratorEvents() {
        return {
            onFatalError: (cb: (...args: Parameters<EventsMap[CollaborationEventTypes.FatalError]>) => void) =>
                this.on(CollaborationEventTypes.FatalError, cb),
            onStatusChange: (cb: (...args: Parameters<EventsMap[CollaborationEventTypes.StatusChange]>) => void) =>
                this.on(CollaborationEventTypes.StatusChange, cb),
        };
    }

    public on<K extends keyof EventsMap>(event: K, cb: EventsMap[K]): Off {
        if (!this.handler[event]) {
            this.handler[event] = [];
        }
        this.handler[event]?.push(cb);
        return () => this.off(event, cb);
    }

    public once<K extends keyof EventsMap>(event: K, cb: EventsMap[K]) {
        const off = this.on(event, (...args: Parameters<EventsMap[K]>) => {
            cb(...args);
            off();
        });
    }

    public emit<K extends keyof EventsMap>(event: K, ...args: Parameters<EventsMap[K]>) {
        const callbackArr = this.handler[event];
        if (!callbackArr?.length) return;
        for (const cb of Array.from(callbackArr)) {
            cb(...args);
        }
    }

    public off<K extends keyof EventsMap>(event: K, cb?: EventsMap[K]) {
        const callbacks = this.handler[event];
        if (!callbacks) return;
        if (cb) {
            const findIndex = callbacks.findIndex((callback) => callback === cb);
            findIndex > -1 && callbacks.splice(findIndex, 1);
            return;
        }
        callbacks.splice(0, callbacks.length);
    }

    public dispose() {
        this.handler = {};
    }
}
